import React, { useState, useEffect } from 'react';
import { obtenerEstadisticasUsuario } from '../../services/userService';
import { handleApiError } from '../../config/apiConfig';
import buildImageUrl from '../../config/imageUrlHelper';

const ROL_LABEL = { admin: 'Administrador', moderador: 'Moderador', usuario: 'Usuario' };
const ROL_CLASS = {
  admin: 'bg-rose-100 text-rose-700 dark:bg-rose-900/60 dark:text-rose-200',
  moderador: 'bg-amber-100 text-amber-700 dark:bg-amber-900/60 dark:text-amber-200',
  usuario: 'bg-sky-100 text-sky-700 dark:bg-sky-900/60 dark:text-sky-200',
};

const UserProfileModal = ({
  mostrarTarjetaUsuario,
  usuarioSeleccionado,
  setMostrarTarjetaUsuario,
  permisoModeracion,
  usuariosConectados,
  esBaneoActivo,
  onBanear,
  onDesbanear,
}) => {
  const [estadisticas, setEstadisticas] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (mostrarTarjetaUsuario && usuarioSeleccionado) {
      setEstadisticas(null);
      setError(null);
      cargarEstadisticas();
    }
  }, [mostrarTarjetaUsuario, usuarioSeleccionado?.id]);

  const cargarEstadisticas = async () => {
    if (!usuarioSeleccionado?.id) return;
    try {
      setCargando(true);
      const response = await obtenerEstadisticasUsuario(usuarioSeleccionado.id);
      if (response.success) {
        setEstadisticas(response.user.stats);
      } else {
        setError(response.message || 'Error al cargar estadísticas');
      }
    } catch (err) {
      setError(handleApiError(err) || 'Error al cargar estadísticas del usuario');
    } finally {
      setCargando(false);
    }
  };

  if (!mostrarTarjetaUsuario || !usuarioSeleccionado) return null;

  const baneado = esBaneoActivo(usuarioSeleccionado);
  const activo = usuariosConectados.has(String(usuarioSeleccionado.id)) && !baneado;
  const estado = baneado ? 'Baneado' : activo ? 'Activo' : 'Inactivo';
  const estadoClass = baneado
    ? 'bg-red-100 text-red-700 dark:bg-red-900/60 dark:text-red-200'
    : activo
    ? 'bg-green-100 text-green-700 dark:bg-green-900/60 dark:text-green-200'
    : 'bg-gray-100 text-gray-600 dark:bg-gray-600 dark:text-gray-300';

  const imageUrl = buildImageUrl(usuarioSeleccionado.foto_perfil);
  const puedeModerar = permisoModeracion === 'admin' ||
    (permisoModeracion === 'moderador' && usuarioSeleccionado.rol === 'usuario');

  const fechaCreacion = usuarioSeleccionado.fecha_registro
    ? new Date(usuarioSeleccionado.fecha_registro).toLocaleDateString()
    : '—';

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center p-3 z-50"
      onClick={() => setMostrarTarjetaUsuario(false)}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-sm max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Cabecera: foto + username + cerrar */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={usuarioSeleccionado.username}
              className="w-12 h-12 rounded-full object-cover shrink-0 border-2 border-gray-200 dark:border-gray-600"
            />
          ) : (
            <div className="w-12 h-12 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-lg font-bold text-gray-600 dark:text-gray-300 shrink-0">
              {(usuarioSeleccionado.username || 'U').charAt(0).toUpperCase()}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-gray-900 dark:text-white truncate leading-tight">
              {usuarioSeleccionado.username}
            </p>
            <span className={`inline-block mt-0.5 px-2 py-0.5 rounded-full text-xs font-medium ${ROL_CLASS[usuarioSeleccionado.rol] || ROL_CLASS.usuario}`}>
              {ROL_LABEL[usuarioSeleccionado.rol] || usuarioSeleccionado.rol}
            </span>
          </div>
          <button
            onClick={() => setMostrarTarjetaUsuario(false)}
            className="shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-xl leading-none p-1"
            aria-label="Cerrar"
          >
            &times;
          </button>
        </div>

        {/* Cuerpo */}
        <div className="px-4 py-3 space-y-3">
          {/* Email */}
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-0.5">Email</p>
            <p className="text-sm text-gray-800 dark:text-gray-100 break-all">{usuarioSeleccionado.email}</p>
          </div>

          {/* Estado + Fecha en la misma fila */}
          <div className="flex gap-4">
            <div className="flex-1">
              <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-0.5">Estado</p>
              <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${estadoClass}`}>
                {estado}
              </span>
            </div>
            <div className="flex-1">
              <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-0.5">Miembro desde</p>
              <p className="text-sm text-gray-800 dark:text-gray-100">{fechaCreacion}</p>
            </div>
          </div>

          {/* Estadísticas en fila horizontal */}
          <div className="border-t border-gray-100 dark:border-gray-700 pt-3">
            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Estadísticas</p>
            {cargando ? (
              <div className="flex justify-center py-3">
                <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500" />
              </div>
            ) : error ? (
              <p className="text-xs text-red-500 text-center py-1">{error}</p>
            ) : estadisticas ? (
              <div className="grid grid-cols-3 gap-2 text-center">
                {[
                  { label: 'Enviadas', value: estadisticas.total_preguntas_enviadas },
                  { label: 'Aceptadas', value: estadisticas.preguntas_aceptadas },
                  { label: 'Rechazadas', value: estadisticas.preguntas_rechazadas },
                ].map(({ label, value }) => (
                  <div key={label} className="bg-gray-50 dark:bg-gray-700 rounded-lg py-2 px-1">
                    <p className="text-xl font-bold text-gray-900 dark:text-white">{value ?? 0}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 leading-tight">{label}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-gray-400 text-center py-1">Sin estadísticas</p>
            )}
          </div>
        </div>

        {/* Pie: botones en la misma fila */}
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60">
          {puedeModerar && !baneado && (
            <button
              onClick={() => onBanear(usuarioSeleccionado.id, 7)}
              className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Banear 7 días
            </button>
          )}
          {puedeModerar && baneado && (
            <button
              onClick={() => onDesbanear(usuarioSeleccionado.id)}
              className="px-3 py-1.5 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
            >
              Desbanear
            </button>
          )}
          <button
            onClick={() => setMostrarTarjetaUsuario(false)}
            className="px-3 py-1.5 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserProfileModal;
